<div class="p-5 max-w-[1600px] mx-auto">

  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <div class="flex items-center gap-3">
      <button mat-icon-button (click)="cancel()" matTooltip="{{ 'reservations.form.cancel' | translate }}" [attr.aria-label]="'reservations.form.cancel' | translate">
        <mat-icon>arrow_back</mat-icon>
      </button>
      @if (reservationId()) {
        <h1 class="text-2xl font-medium">{{ 'reservations.form.edit-title' | translate }} {{ detail()?.reservationNumber }}</h1>
      } @else {
        <h1 class="text-2xl font-medium">{{ 'reservations.form.create-title' | translate }}</h1>
      }
    </div>
    <div class="flex gap-2 items-center">
      @if (saving()) {
        <mat-spinner diameter="24" />
      }
      <button mat-stroked-button (click)="cancel()" [disabled]="saving()">
        {{ 'reservations.form.cancel' | translate }}
      </button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="saving() || loading()">
        {{ 'reservations.form.save' | translate }}
      </button>
    </div>
  </div>

  @if (loading()) {
    <div class="flex justify-center py-20">
      <mat-spinner diameter="48" />
    </div>
  } @else {

    <form class="flex gap-5 items-start" [formGroup]="form" (ngSubmit)="save()">

      <!-- Left column (2/3) -->
      <div class="flex-[2] flex flex-col gap-5">

        <!-- Customer -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.form.section.customer' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="flex gap-4">
              <mat-form-field class="flex-[2]">
                <mat-label>{{ 'reservations.field.customer' | translate }}</mat-label>
                <input matInput formControlName="customer" [matAutocomplete]="auto"
                  placeholder="{{ 'reservations.form.customer-search' | translate }}" />
                <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayCustomer">
                  @for (c of customerOptions(); track c.id) {
                    <mat-option [value]="pickCustomer(c)">{{ c.firstName }} {{ c.lastName }}@if (c.email) { <span class="text-gray-400 text-xs"> — {{ c.email }}</span> }</mat-option>
                  }
                </mat-autocomplete>
                <mat-error>{{ 'reservations.form.customer-required' | translate }}</mat-error>
              </mat-form-field>

              <mat-form-field class="flex-1">
                <mat-label>{{ 'reservations.field.type' | translate }}</mat-label>
                <mat-select formControlName="reservationType">
                  <mat-option value="private">{{ 'reservations.type.private' | translate }}</mat-option>
                  <mat-option value="business">{{ 'reservations.type.business' | translate }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            @if (isBusiness) {
              <mat-form-field class="w-full">
                <mat-label>{{ 'reservations.field.companyName' | translate }}</mat-label>
                <input matInput formControlName="companyName" />
              </mat-form-field>
            }

            <mat-form-field class="w-full">
              <mat-label>{{ 'reservations.field.createdOn' | translate }}</mat-label>
              <input matInput [matDatepicker]="bookingDatePicker" formControlName="createdOn" />
              <mat-datepicker-toggle matIconSuffix [for]="bookingDatePicker" />
              <mat-datepicker #bookingDatePicker />
              @if (form.controls.createdOn.touched && form.controls.createdOn.invalid) {
                <mat-error>{{ 'reservations.form.booking-date-required' | translate }}</mat-error>
              }
            </mat-form-field>

            @if (detail()?.primaryEmail) {
              <p class="text-xs text-gray-500">{{ 'reservations.form.notify-email' | translate }}: {{ detail()?.primaryEmail }}</p>
            }
          </mat-card-content>
        </mat-card>

        <!-- Lake & pricing -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.form.section.lake-pricing' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="flex gap-4">
              <mat-form-field class="flex-1">
                <mat-label>{{ 'reservations.field.site' | translate }}</mat-label>
                <mat-select formControlName="siteId">
                  @for (s of codeListsService.sites(); track s.id) { <mat-option [value]="s.id">{{ s.name }}</mat-option> }
                </mat-select>
                @if (form.controls.siteId.touched && form.controls.siteId.invalid) {
                  <mat-error>{{ 'reservations.form.site-required' | translate }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="flex-[2]">
                <mat-label>{{ 'reservations.field.water' | translate }}</mat-label>
                <mat-select formControlName="waterId" (openedChange)="onWaterPanelOpen($event)">
                  <div class="px-3 py-2 border-b border-gray-200">
                    <input
                      class="block w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                      [value]="waterSearchQuery()"
                      (keydown)="$event.stopPropagation()"
                      (input)="onWaterSearch($event)"
                      placeholder="{{ 'reservations.form.water-search' | translate }}" />
                  </div>
                  @for (w of filteredWaters(); track w.id) { <mat-option [value]="w.id">{{ w.name }}</mat-option> }
                </mat-select>
                @if (form.controls.waterId.touched && form.controls.waterId.invalid) {
                  <mat-error>{{ 'reservations.form.water-required' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="flex gap-4">
              <mat-form-field class="flex-1">
                <mat-label>{{ 'reservations.field.purchasePrice' | translate }}</mat-label>
                <span matTextPrefix>€&nbsp;</span>
                <input matInput type="number" step="0.01" formControlName="purchasePrice" />
              </mat-form-field>

              <mat-form-field class="flex-1">
                <mat-label>{{ 'reservations.field.retailPrice' | translate }}</mat-label>
                <span matTextPrefix>€&nbsp;</span>
                <input matInput type="number" step="0.01" formControlName="retailPrice" />
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Options -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.form.section.options' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'reservations.field.lastMinute' | translate }}</span>
                <mat-slide-toggle formControlName="lastMinute" />
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'reservations.field.insurance' | translate }}</span>
                <mat-slide-toggle formControlName="insurance" />
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'reservations.field.dog' | translate }}</span>
                <mat-slide-toggle formControlName="dog" />
              </div>
              <!-- Full-width row directly under the dog toggle; keeps the toggle grid aligned. -->
              @if (form.controls.dog.value) {
                <mat-form-field class="col-span-2" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.dogBreed' | translate }}</mat-label>
                  <input matInput formControlName="dogBreed" />
                </mat-form-field>
              }
              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'reservations.field.financial' | translate }}</span>
                <mat-slide-toggle formControlName="financial" />
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Payment & financial number / buy number / type and invoice dates are system-managed
             (Mollie / Exact / invoicing) and are not edited here — see the list for those columns. -->

        <!-- Other -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.form.section.other' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <mat-form-field class="w-full">
              <mat-label>{{ 'reservations.field.emergencyContact' | translate }}</mat-label>
              <input matInput formControlName="emergencyContact" />
            </mat-form-field>
            <mat-form-field class="w-full">
              <mat-label>{{ 'reservations.field.comment' | translate }}</mat-label>
              <textarea matInput rows="3" formControlName="reservationComment"></textarea>
            </mat-form-field>
          </mat-card-content>
        </mat-card>

        <!-- Accommodations and swims are editable inline rows (both create and edit). -->
        <!-- Accommodations — editable inline rows -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.form.section.accommodations' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @for (group of accommodations.controls; track $index; let i = $index) {
              <div class="flex gap-2 items-start mb-2" [formGroup]="group">
                <mat-form-field class="flex-[2]" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.accommodation' | translate }}</mat-label>
                  <mat-select formControlName="accommodationId">
                    @for (a of accommodationOptions(); track a.id) {
                      <mat-option [value]="a.id">{{ a.name ?? '#' + a.number }}</mat-option>
                    }
                  </mat-select>
                  @if (group.controls.accommodationId.touched && group.controls.accommodationId.hasError('required')) {
                    <mat-error>{{ 'reservations.accommodations.accommodation-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field class="w-20" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.anglers' | translate }}</mat-label>
                  <input matInput type="number" min="0" formControlName="fisherMan" required />
                </mat-form-field>

                <mat-form-field class="w-20" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.nonAnglers' | translate }}</mat-label>
                  <input matInput type="number" min="0" formControlName="nonFisherMan" required />
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.arrival' | translate }}</mat-label>
                  <input matInput [matDatepicker]="accFromPicker" formControlName="arrivalDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="accFromPicker" />
                  <mat-datepicker #accFromPicker />
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.departure' | translate }}</mat-label>
                  <input matInput [matDatepicker]="accToPicker" formControlName="departureDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="accToPicker" />
                  <mat-datepicker #accToPicker />
                </mat-form-field>

                <button mat-icon-button type="button" color="warn" (click)="removeAccommodation(i)"
                  matTooltip="{{ 'reservations.accommodations.remove' | translate }}">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>

              @if (group.hasError('invalidDateRange')) {
                <p class="text-sm text-red-600 mb-2">{{ 'reservations.accommodations.arrival-before-departure' | translate }}</p>
              }
            } @empty {
              <p class="text-sm text-gray-500 mb-2">{{ 'reservations.form.no-accommodations' | translate }}</p>
            }

            @if (accommodations.hasError('overlappingAccommodation')) {
              <p class="text-sm text-red-600 mb-2">{{ 'reservations.accommodations.duplicate' | translate }}</p>
            }

            <button mat-stroked-button type="button" class="mt-1" (click)="addAccommodation()">
              <mat-icon>add</mat-icon>
              {{ 'reservations.accommodations.add' | translate }}
            </button>
          </mat-card-content>
        </mat-card>

        <!-- Swims (fishing spots) — editable inline rows -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.form.section.swims' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @for (group of fishingSpots.controls; track $index; let i = $index) {
              <div class="flex gap-2 items-start mb-2" [formGroup]="group">
                <!-- Per row (CS-228): this line rents the whole lake for its own date range, so the
                     reservation can mix a lake week with a named-swim week. Locked on for an
                     exclusive-only water, which is never sold per swim. -->
                <div class="flex flex-col items-center self-center w-24">
                  <span class="text-xs text-gray-500">{{ 'reservations.field.wholeWater' | translate }}</span>
                  <mat-slide-toggle formControlName="wholeWater" />
                </div>

                @if (hasSelectableSwims() && !group.controls.wholeWater.value) {
                  <mat-form-field class="flex-[2]" subscriptSizing="dynamic">
                    <mat-label>{{ 'reservations.field.fishingSpot' | translate }}</mat-label>
                    <mat-select formControlName="fishingSpotId">
                      @for (s of fishingSpotOptions(); track s.id) {
                        <!-- Match the legacy label / read-side FishingSpotName: "{title} - {number}". -->
                        <mat-option [value]="s.id">{{ s.number !== null ? s.title + ' - ' + s.number : s.title }}</mat-option>
                      }
                    </mat-select>
                    @if (group.controls.fishingSpotId.touched && group.controls.fishingSpotId.hasError('required')) {
                      <mat-error>{{ 'reservations.swims.fishing-spot-required' | translate }}</mat-error>
                    }
                  </mat-form-field>
                } @else {
                  <!-- Whole-water line or a water with no selectable swims: the swim is left unset (dummy). -->
                  <div class="flex-[2] text-sm text-gray-500 self-center">
                    {{ 'reservations.swims.whole-water' | translate }}
                  </div>
                }

                <mat-form-field class="w-20" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.anglers' | translate }}</mat-label>
                  <input matInput type="number" min="0" formControlName="fisherMan" required />
                </mat-form-field>

                <mat-form-field class="w-20" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.nonAnglers' | translate }}</mat-label>
                  <input matInput type="number" min="0" formControlName="nonFisherMan" required />
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.arrival' | translate }}</mat-label>
                  <input matInput [matDatepicker]="swimFromPicker" formControlName="arrivalDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="swimFromPicker" />
                  <mat-datepicker #swimFromPicker />
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.field.departure' | translate }}</mat-label>
                  <input matInput [matDatepicker]="swimToPicker" formControlName="departureDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="swimToPicker" />
                  <mat-datepicker #swimToPicker />
                </mat-form-field>

                <button mat-icon-button type="button" color="warn" (click)="removeFishingSpot(i)"
                  matTooltip="{{ 'reservations.swims.remove' | translate }}">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>

              @if (group.hasError('invalidDateRange')) {
                <p class="text-sm text-red-600 mb-2">{{ 'reservations.swims.arrival-before-departure' | translate }}</p>
              }
            } @empty {
              <p class="text-sm text-gray-500 mb-2">{{ 'reservations.form.no-swims' | translate }}</p>
            }

            @if (fishingSpots.hasError('overlappingFishingSpot')) {
              <p class="text-sm text-red-600 mb-2">{{ 'reservations.swims.duplicate' | translate }}</p>
            }

            <!-- Always offered: since the whole-water flag is per row (CS-228), several rows are
                 meaningful even on a whole-water booking — one can rent the lake for a week while the
                 next takes a named swim. -->
            <button mat-stroked-button type="button" class="mt-1" (click)="addFishingSpot()">
              <mat-icon>add</mat-icon>
              {{ 'reservations.swims.add' | translate }}
            </button>
          </mat-card-content>
        </mat-card>

        <!-- Co-travelers ("Medereiziger(s)") -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.coTravelers.section' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @for (group of coTravelers.controls; track $index; let i = $index) {
              <div class="flex gap-2 items-center mb-2" [formGroup]="group">
                <mat-form-field class="flex-[2]" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.coTravelers.customer' | translate }}</mat-label>
                  <input matInput formControlName="customer" [matAutocomplete]="companionAuto"
                    placeholder="{{ 'reservations.coTravelers.customer-search' | translate }}" />
                  <mat-autocomplete #companionAuto="matAutocomplete" [displayWith]="displayCustomer">
                    @for (c of coTravelerOptions(); track c.id) {
                      <mat-option [value]="pickCustomer(c)">{{ c.firstName }} {{ c.lastName }}@if (c.email) { <span class="text-gray-400 text-xs"> — {{ c.email }}</span> }</mat-option>
                    }
                  </mat-autocomplete>
                  @if (group.controls.customer.touched && group.controls.customer.hasError('requireSelection')) {
                    <mat-error>{{ 'reservations.coTravelers.customer-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'reservations.coTravelers.type' | translate }}</mat-label>
                  <mat-select formControlName="periodTargetId">
                    @for (t of codeListsService.coTravelerTypes(); track t.id) {
                      <mat-option [value]="t.id">{{ t.name }}</mat-option>
                    }
                  </mat-select>
                  @if (group.controls.periodTargetId.touched && group.controls.periodTargetId.hasError('required')) {
                    <mat-error>{{ 'reservations.coTravelers.type-required' | translate }}</mat-error>
                  }
                </mat-form-field>

                <button mat-icon-button type="button" color="warn" (click)="removeCoTraveler(i)"
                  matTooltip="{{ 'reservations.coTravelers.remove' | translate }}">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            } @empty {
              <p class="text-sm text-gray-500 mb-2">{{ 'reservations.coTravelers.empty' | translate }}</p>
            }

            @if (coTravelers.hasError('duplicateCoTraveler')) {
              <p class="text-sm text-red-600 mb-2">{{ 'reservations.coTravelers.duplicate' | translate }}</p>
            }

            <button mat-stroked-button type="button" class="mt-1" (click)="addCoTraveler()">
              <mat-icon>add</mat-icon>
              {{ 'reservations.coTravelers.add' | translate }}
            </button>
          </mat-card-content>
        </mat-card>

        <!-- TODO: CS-204 — manage reservation notes. -->
      </div>

      <!-- Right column (1/3) -->
      <div class="flex-1 flex flex-col gap-5">

        <!-- Status -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'reservations.field.status' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <mat-form-field class="w-full">
              <mat-label>{{ 'reservations.field.status' | translate }}</mat-label>
              <mat-select formControlName="statusId">
                @for (s of codeListsService.reservationStatuses(); track s.id) { <mat-option [value]="s.id">{{ s.name }}</mat-option> }
              </mat-select>
              @if (form.controls.statusId.touched && form.controls.statusId.invalid) {
                <mat-error>{{ 'reservations.form.status-required' | translate }}</mat-error>
              }
            </mat-form-field>
            <!-- TODO: CS-203 — status-transition workflow (logging, mails, last-minute) replaces this plain field set. -->
          </mat-card-content>
        </mat-card>

        <!-- Details summary (edit mode only) -->
        @if (reservationId()) {
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'reservations.form.section.details' | translate }}</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="flex flex-col gap-3 pt-2 text-sm">
                <div class="flex flex-col">
                  <span class="text-xs text-gray-500">{{ 'reservations.field.reservationNumber' | translate }}</span>
                  <span class="font-medium">{{ detail()?.reservationNumber }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-500">{{ 'reservations.form.stay' | translate }}</span>
                  <span>
                    {{ detail()?.arrivalDate ? (detail()?.arrivalDate | date: 'mediumDate') : '—' }}
                    →
                    {{ detail()?.departureDate ? (detail()?.departureDate | date: 'mediumDate') : '—' }}
                  </span>
                  <span class="text-xs text-gray-400">{{ 'reservations.form.stay-hint' | translate }}</span>
                </div>
                @if (detail()?.cancelledOn) {
                  <div class="flex flex-col text-red-700">
                    <span class="text-xs">{{ 'reservations.field.cancelledOn' | translate }}</span>
                    <span>{{ detail()?.cancelledOn | date: 'mediumDate' }}</span>
                  </div>
                }
              </div>
            </mat-card-content>
          </mat-card>
        }

      </div>

    </form>

  }

</div>
